import { useMemo, useRef, useState } from "react";
import { StarIcon, StarOffIcon } from "lucide-react";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/Base/Popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/Base/Tabs";
import { ColorPicker } from "@/components/Dashboard/components/ColorPicker/ColorPicker";
import { NotesMain } from "@/components/Dashboard/components/Card/Notes.Main";
import { RoTasksSection } from "@/components/Tasks/RoTasksSection";
import { AttachmentsMain } from "@/components/Common/Attachments/AttachmentsMain";
import { TagsPanel } from "@/components/Dashboard/components/Card/Card.TagsPanel";
import { cardAttachmentsPath, cardPath } from "@/repositories/attachment";
import { taskCounts, tasksForCard } from "@/lib/taskSelectors";
import { useStore } from "@/stores/useStore";
import { cn } from "@/lib/utils";
import { Card, HexColor, Task } from "@/types/v2";
import {
  ACTIVITY_TABS,
  ActivityButton,
  ActivityCounts,
  ActivitySegment,
  ActivityTab,
  activityCount,
  formatCount,
} from "@/components/Dashboard/components/Card/Card.ActivityButton";

// Stable empty list: with Tasks off the store subscription selects this, so a
// task-store change never re-renders a card that cannot show tasks.
const NO_TASKS: Task[] = [];

const TAB_LABELS: Record<ActivityTab, string> = {
  notes: "Notes",
  tasks: "Tasks",
  files: "Files",
  tags: "Tags",
};

export interface CardActivityProps {
  cardData: Card;
  shopId: string;
  disabled?: boolean;
  overdue?: boolean;
  compactNotes?: boolean;
  isNotesEnabled?: boolean;
  isTasksEnabled?: boolean;
  isFilesEnabled?: boolean;
  isColorEnabled?: boolean;
  color: HexColor;
  onColorChange?: (color: HexColor) => void;
  onStarToggle?: () => void;
}

export const CardActivity = ({
  cardData,
  shopId,
  disabled,
  overdue = false,
  compactNotes,
  isNotesEnabled,
  isTasksEnabled,
  isFilesEnabled,
  isColorEnabled,
  color,
  onColorChange,
  onStarToggle,
}: CardActivityProps) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ActivityTab>("notes");
  // Tabs whose panel has been shown since the popover opened. A panel mounts on
  // first visit and then STAYS mounted (hidden) so an unsent note draft
  // survives a tab switch; unvisited panels stay unmounted so opening on Tasks
  // neither steals focus into the notes composer nor starts the attachments
  // query. Reset on close - Radix unmounts the content then anyway.
  const [visited, setVisited] = useState<ActivityTab[]>([]);
  // The tab the popover was OPENED on. Only that one may autofocus the notes
  // composer; reaching Notes later by arrowing the tablist must not.
  const [openedTab, setOpenedTab] = useState<ActivityTab | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  // Mirrors Radix's own hasInteractedOutsideRef: a genuine outside press means
  // focus belongs to whatever was pressed, so close must not pull it back.
  const interactedOutside = useRef(false);
  const allTasks = useStore((s) => (isTasksEnabled ? s.tasks : NO_TASKS));

  const enabled: Record<ActivityTab, boolean> = {
    notes: !!isNotesEnabled && !!shopId,
    tasks: !!isTasksEnabled,
    files: !!isFilesEnabled && !!shopId,
    tags: true,
  };
  const tabs = ACTIVITY_TABS.filter((tab) => enabled[tab]);
  const shownTab = tabs.includes(activeTab) ? activeTab : tabs[0];

  const { completed, total } = useMemo(
    () => taskCounts(tasksForCard(allTasks, cardData.id)),
    [allTasks, cardData.id]
  );
  const counts: ActivityCounts = {
    notes: cardData.notes?.length ?? 0,
    tasksTotal: total,
    tasksOpen: total - completed,
    files: cardData.attachmentCount ?? 0,
    tags: cardData.tags?.length ?? 0,
  };

  const show = (tab: ActivityTab) => {
    setActiveTab(tab);
    setVisited((v) => (v.includes(tab) ? v : [...v, tab]));
  };
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setVisited([]);
  };
  const handleSegmentClick = (tab: ActivityTab) => {
    if (open && shownTab === tab) {
      handleOpenChange(false);
      return;
    }
    if (!open) setOpenedTab(tab);
    show(tab);
    setOpen(true);
  };

  const starred = !!cardData.isStarred;

  const renderPanel = (tab: ActivityTab) => {
    if (tab === "notes") {
      return (
        <NotesMain
          shopId={shopId}
          cardData={cardData}
          disabled={disabled}
          autoFocusNewNote={openedTab === "notes"}
        />
      );
    }
    if (tab === "tasks") {
      return (
        <RoTasksSection
          cardId={cardData.id}
          showCompleted
          className="border-0 bg-transparent p-0"
        />
      );
    }
    if (tab === "tags") {
      return (
        <TagsPanel cardData={cardData} shopId={shopId} disabled={disabled} />
      );
    }
    return (
      <AttachmentsMain
        shopId={shopId}
        entityPath={cardPath(shopId, cardData.id)}
        attachmentsPath={cardAttachmentsPath(shopId, cardData.id)}
        canModify={!disabled}
      />
    );
  };

  return (
    <Popover open={open && !!shownTab} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <ActivityButton
          ref={anchorRef}
          tabs={tabs}
          expandedTab={open ? shownTab : undefined}
          counts={counts}
          overdue={overdue}
          disabled={disabled}
          compactNotes={compactNotes}
          onSegmentClick={handleSegmentClick}
          onAddClick={handleSegmentClick}
        >
          {isColorEnabled ? (
            <ColorPicker
              color={color}
              onColorChange={onColorChange}
              disabled={disabled}
              trigger={
                <ActivitySegment
                  label="Card color"
                  state="plain"
                  className="px-px"
                >
                  <span
                    className="block size-2.5 rounded-[1px] border border-[#1e3a8a]"
                    style={{ backgroundColor: color }}
                  />
                </ActivitySegment>
              }
            />
          ) : (
            <ActivitySegment
              label={starred ? "Unstar card" : "Star card"}
              state={starred ? "starred" : "active"}
              onClick={onStarToggle}
            >
              {starred ? <StarIcon size={12} /> : <StarOffIcon size={12} />}
            </ActivitySegment>
          )}
        </ActivityButton>
      </PopoverAnchor>
      {shownTab && (
        <PopoverContent
          align="end"
          onMouseDown={(e) => e.stopPropagation()}
          // A tab segment is outside this content, so Radix would dismiss on
          // its pointerdown/focus and the segment's click would then REOPEN it
          // - the active segment could never close the popover. Treat presses
          // on the tab segments as inside; handleSegmentClick does the toggle.
          // (The color segment is deliberately NOT exempt: opening the color
          // grid should close this popover.)
          // Scoped to THIS card's anchor: another card's segments are a
          // genuine outside press and must dismiss this popover.
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement | null;
            if (
              target &&
              anchorRef.current?.contains(target) &&
              target.closest?.("[data-activity-tab-segment]")
            ) {
              e.preventDefault();
              return;
            }
            interactedOutside.current = true;
          }}
          // PopoverAnchor is not a PopoverTrigger, so Radix has no trigger to
          // return focus to (it would focus null, then preventDefault). Do it
          // ourselves, unless the user pressed somewhere else to dismiss:
          // the shown tab's segment if it is rendered, else the + (the tab was
          // reached through it, or its last item was removed while open), else
          // the first enabled cell. Resolved at close time because segments
          // appear and disappear while the popover is open.
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (!interactedOutside.current) {
              const root = anchorRef.current;
              (
                root?.querySelector<HTMLElement>(
                  `[data-activity-tab="${shownTab}"]`
                ) ??
                root?.querySelector<HTMLElement>("[data-activity-add]") ??
                root?.querySelector<HTMLElement>("button:not([aria-disabled])")
              )?.focus();
            }
            interactedOutside.current = false;
          }}
          className="max-h-[500px] w-96 overflow-y-auto bg-card p-0"
        >
          <Tabs value={shownTab} onValueChange={(v) => show(v as ActivityTab)}>
            <TabsList
              className={cn(
                "sticky top-0 z-10 bg-card",
                "flex h-auto w-full rounded-none border-b border-[#e2e8f0] p-0"
              )}
            >
              {tabs.map((tab) => {
                const active = tab === shownTab;
                return (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className="flex-1 gap-1.5 rounded-none border-0 border-b-2 border-transparent px-2 py-2 text-[13px] font-semibold text-[#64748b] data-[state=active]:border-[#1e3a8a] data-[state=active]:bg-transparent data-[state=active]:text-[#1e3a8a] data-[state=active]:shadow-none"
                  >
                    {TAB_LABELS[tab]}
                    <span
                      className={cn(
                        "rounded-full px-1.5 text-[11px] leading-4",
                        active
                          ? "bg-[#1e3a8a] text-white"
                          : "bg-[#e2e8f0] text-[#475569]"
                      )}
                    >
                      {formatCount(activityCount(tab, counts))}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {tabs.map((tab) =>
              visited.includes(tab) || tab === shownTab ? (
                // forceMount keeps an inactive panel mounted, but Radix does
                // NOT set `hidden` when forceMount is on - hide it by state.
                <TabsContent
                  key={tab}
                  value={tab}
                  forceMount
                  className="mt-0 flex flex-col gap-2 p-2 data-[state=inactive]:hidden"
                >
                  {renderPanel(tab)}
                </TabsContent>
              ) : null
            )}
          </Tabs>
        </PopoverContent>
      )}
    </Popover>
  );
};
